import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { BoardSubheader } from "@/components/board/BoardSubheader";
import { BoardToolbar, type BoardView } from "@/components/board/BoardToolbar";
import { TableView, type Density } from "@/components/board/TableView";
import type { CopyRowOpts } from "@/components/board/RowCopyMenu";
import { KanbanView } from "@/components/board/KanbanView";
import { CalendarView } from "@/components/board/CalendarView";
import { TimelineView } from "@/components/board/TimelineView";
import { RowDetailPanel } from "@/components/board/RowDetailPanel";
import { DetailSplitter } from "@/components/board/DetailSplitter";
import { MobileBoardView } from "@/components/board/mobile/MobileBoardView";
import { Icon } from "@/components/ui/Icon";
import type { PillStyle } from "@/components/ui/StatusPill";
import { t } from "@/lib/strings";
import { showToast } from "@/lib/toast";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useDetailPaneRatio } from "@/hooks/useDetailPaneRatio";
import { useDetailViewMode } from "@/hooks/useDetailViewMode";
import { useUpdateBoard } from "@/hooks/useUpdateBoard";
import { deriveSectionYear, isTicketBoard, resolveInitialYearTab } from "@/lib/boardView";
import type { ViewBoard, ViewColumn, ViewRow } from "@/lib/boardView";
import { FILTERABLE_TYPES, filterRows } from "@/lib/boardFilters";
import { planGroupDelete, reconcileYearFilter } from "@/lib/groupDelete";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { SORTABLE_TYPES, TICKET_NUMBER_SORT_ID, sortRows } from "@/lib/boardSort";
import {
  loadFilters,
  loadStoredSort,
  resolveInitialSort,
  saveFilters,
  saveSort,
} from "@/lib/boardViewPrefs";
import type {
  CellValue,
  ColumnDef,
  ColumnUpdatePayload,
  FilterCond,
  SortSpec,
  UUID,
  ViewConfig,
} from "@/lib/types";

interface BoardSurfaceProps {
  board: ViewBoard;
  density?: Density;
  pillStyle?: PillStyle;
  view: BoardView;
  setView: (view: BoardView) => void;
  onPatchRow: (id: string, patch: Partial<ViewRow>) => void;
  onAddRow?: (afterRowId?: UUID | null, inGroup?: { columnId: UUID; value: CellValue }) => void;
  /** Generic per-cell text write — used for non-title text columns. */
  onPatchCell?: (rowId: string, columnDefId: string, value: CellValue) => void;
  /** Board-admin-only inline-create for status + tags column options. */
  onAddColumnOption?: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
  canAddColumnOption?: boolean;
  /** Drag-to-reorder row writes. */
  onRowPositionChange?: (rowId: UUID, position: number) => void;
  /** Reparent/promote a sub-item: PATCH parent_row_id + position together. */
  onMoveRow?: (rowId: UUID, parentRowId: UUID | null, position: number) => void;
  /** Column reorder + resize writes. */
  onColumnUpdate?: (columnId: UUID, payload: ColumnUpdatePayload) => void;
  /** Sub-items: mint a new row as a child of the given parent. */
  onAddSubitem?: (parentRowId: UUID) => void;
  /** Unteraufgaben (detail panel + mobile sheet): mint a child row AND write its title and
   *  assignee in one step, so a sub-task is never briefly untitled/unassigned. */
  onCreateSubtask?: (parentRowId: UUID, title: string, assigneeId: string | null) => void;
  /** Soft-delete a row (undoable via the board command stack). */
  onDeleteRow?: (rowId: UUID) => void;
  /** Duplicate a row, optionally including its comments. */
  onDuplicateRow?: (rowId: UUID, withComments: boolean) => void;
  onCopyRow?: (rowId: UUID, opts: CopyRowOpts) => void;
  onDuplicateGroup?: (
    groupColumnId: string,
    sourceValue: unknown,
    newLabel: string,
    withComments: boolean,
  ) => void;
  /** Delete a whole status group: its top-level rows (sub-items cascade) + archive the
   *  option, as one undoable step. Called after the confirm dialog. */
  onDeleteGroupRows?: (groupColumnId: UUID, optionId: string, rowIds: UUID[]) => void;
  // Undo/redo (board-level command stack lives in BoardPage).
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  /** Opens the manual "Neues Ticket" dialog (ticket boards only). */
  onNewTicket?: () => void;
  // Real backend ids; null in fixture mode so subcomponents fall back to toasts.
  boardId?: string | null;
  workspaceId?: string | null;
  // Deep-link: opens RowDetailPanel for this row on mount + when prop changes.
  openRowId?: string | null;
  // Jump-to-comment (①): the comment to scroll to + flash once the panel opens.
  openCommentId?: string | null;
  openCommentQuery?: string | null;
  // Fires when the open row changes (used to keep the URL `?row=` param in sync).
  onDetailChange?: (rowId: string | null) => void;
  // Fires once the comment focus has been captured, so the parent can clear
  // `?comment=` from the URL (a re-render / manual close must not re-flash).
  onCommentConsumed?: () => void;
  // Just-created row → TableView auto-focuses its title + scrolls it into view.
  focusRowId?: string | null;
  onFocusConsumed?: () => void;
}

export function BoardSurface({
  board,
  density = "comfy",
  pillStyle = "tint",
  view,
  setView,
  onPatchRow,
  onAddRow,
  onPatchCell,
  onAddColumnOption,
  canAddColumnOption = false,
  onRowPositionChange,
  onMoveRow,
  onColumnUpdate,
  onAddSubitem,
  onCreateSubtask,
  onDeleteRow,
  onDuplicateRow,
  onCopyRow,
  onDuplicateGroup,
  onDeleteGroupRows,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onNewTicket,
  boardId = null,
  workspaceId = null,
  openRowId = null,
  openCommentId = null,
  openCommentQuery = null,
  onDetailChange,
  onCommentConsumed,
  focusRowId = null,
  onFocusConsumed,
}: BoardSurfaceProps) {
  const isDesktop = useBreakpoint("md");
  const [detailRow, setDetailRow] = useState<ViewRow | null>(null);
  // "Gruppe löschen" awaiting confirmation. The plan is computed from the FULL board
  // (`board.rows`), never the filtered table view — the whole table goes, and the count
  // in the dialog must say so.
  const [groupDelete, setGroupDelete] = useState<{
    groupColumnId: string;
    option: { id: string; label: string };
  } | null>(null);
  const groupDeletePlan = groupDelete
    ? planGroupDelete(board.rows, groupDelete.groupColumnId, groupDelete.option.id)
    : null;

  // Resizable split between the board and the (docked) detail panel. The ratio
  // is per-user (localStorage) + shared across all boards/views; it only applies
  // while the detail panel is open — closed, the board stays full width.
  const [detailRatio, setDetailRatio] = useDetailPaneRatio();
  // ⑤ presentation: "large" swaps the docked split for a centered modal (desktop only).
  const [detailView, setDetailView] = useDetailViewMode();
  const largeDetail = detailView === "large";
  const splitRef = useRef<HTMLDivElement | null>(null);
  const [resizing, setResizing] = useState(false);
  const startResize = (e: ReactMouseEvent) => {
    e.preventDefault();
    const el = splitRef.current;
    if (!el) return;
    setResizing(true);
    const onMove = (ev: globalThis.MouseEvent) => {
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      setDetailRatio((rect.right - ev.clientX) / rect.width);
    };
    const onUp = () => {
      setResizing(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Shared, team-wide view config lives on the board (server-side). Grouping +
  // column visibility both read from / write to board.view_config via PATCH.
  const updateBoard = useUpdateBoard();
  const vc: ViewConfig = board.viewConfig ?? {};
  const GROUPABLE = ["status", "tags", "text", "checkbox"];
  const groupableColumns = board.columns
    .filter((c) => GROUPABLE.includes(c.type))
    .map((c) => ({ id: c.id, name: c.name }));
  const allColumns = board.columns.map((c) => ({ id: c.id, name: c.name }));
  const hiddenColumnIds = vc.hidden_column_ids ?? [];
  // Drop a stale grouping if that column no longer exists on the board.
  const effectiveGroupBy =
    vc.group_by_column_id && board.columns.some((c) => c.id === vc.group_by_column_id)
      ? vc.group_by_column_id
      : null;

  function patchViewConfig(patch: Partial<ViewConfig>) {
    if (!boardId || !workspaceId) return;
    updateBoard.mutate({
      boardId: boardId as UUID,
      workspaceId: workspaceId as UUID,
      payload: { view_config: { ...vc, ...patch } },
    });
  }
  function changeGroupBy(id: string | null) {
    patchViewConfig({ group_by_column_id: id });
  }
  // Kanban lanes bucket by a status column — chosen via the picker, else the
  // first status column. Independent of the table grouping.
  const statusColumns = board.columns.filter((c) => c.type === "status");
  const effectiveKanbanCol =
    vc.kanban_column_id && statusColumns.some((c) => c.id === vc.kanban_column_id)
      ? vc.kanban_column_id
      : (statusColumns[0]?.id ?? null);
  function changeKanbanColumn(id: string) {
    patchViewConfig({ kanban_column_id: id });
  }
  function toggleColumn(id: string) {
    const set = new Set(hiddenColumnIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    patchViewConfig({ hidden_column_ids: Array.from(set) });
  }

  // Filters + sort are PER-USER (localStorage), NOT shared on the board — one person's
  // filter must never be imposed on colleagues (they used to live in view_config.filters/
  // .sort). Kept per board and re-hydrated when the board changes, mirroring the year-tab
  // pattern below. The detail panel + year-tabs still use the FULL board.rows.
  const [filters, setFilters] = useState<FilterCond[]>(() => loadFilters(board.id));
  const [storedSort, setStoredSort] = useState<SortSpec | null | undefined>(() =>
    loadStoredSort(board.id),
  );
  useEffect(() => {
    setFilters(loadFilters(board.id));
    setStoredSort(loadStoredSort(board.id));
  }, [board.id]);
  const filterColumns = board.columns.filter((c) => FILTERABLE_TYPES.includes(c.type));
  const filteredRows = filterRows(board.rows, filters, board.columns);
  function changeFilters(next: FilterCond[]) {
    setFilters(next);
    saveFilters(board.id, next);
  }
  // Sort applied after filtering (within groups/lanes). Ticket boards get a virtual
  // "Ticket-ID" sort and default to newest-first until the user picks another sort; a stored
  // `null` = the user turned sorting off (respected, no snap-back). See resolveInitialSort.
  const ticketBoard = isTicketBoard(board);
  const sort: SortSpec | null = resolveInitialSort(storedSort, ticketBoard);
  const ticketSortColumn: ViewColumn = {
    id: TICKET_NUMBER_SORT_ID,
    type: "number",
    name: t.sortTicketId,
    width: 0,
  };
  const sortColumns = [
    ...(ticketBoard ? [ticketSortColumn] : []),
    ...board.columns.filter((c) => SORTABLE_TYPES.includes(c.type)),
  ];
  const viewRows = sortRows(filteredRows, sort, board.columns);
  function changeSort(next: SortSpec | null) {
    setStoredSort(next);
    saveSort(board.id, next);
  }

  // Columns shown in the views: hide configured ones, but never hide the column
  // we're actively grouping by (it must resolve for the section headers).
  const visibleColumns = board.columns.filter(
    (c) => !hiddenColumnIds.includes(c.id) || c.id === effectiveGroupBy,
  );
  const displayBoard: ViewBoard = { ...board, columns: visibleColumns, rows: viewRows };
  // The TABLE may ALSO hide the group-by column — its value is already shown in
  // each section header, so a per-row Bereich cell is redundant. Other views keep
  // displayBoard (which forces it visible). Grouping still works via groupColumn.
  const tableBoard: ViewBoard = {
    ...board,
    columns: board.columns.filter((c) => !hiddenColumnIds.includes(c.id)),
    rows: viewRows,
  };
  const groupColumn = effectiveGroupBy
    ? (board.columns.find((c) => c.id === effectiveGroupBy) ?? null)
    : null;
  // Guests can only edit their assigned rows' cells — no structural ops. Hide
  // the add-row / add-column / add-subitem affordances (the backend 403s them
  // anyway; this removes the dead buttons).
  const isGuest = board.access === "guest";
  // Ticket boards hide project-isms (year-tabs derive from section Zieltermine,
  // which tickets don't use). Same flag as `ticketBoard` above.
  const isTickets = ticketBoard;

  // Year-tabs: the years of sections that actually CONTAIN visible rows (from
  // each section option's Zieltermin, else parsed from its label). Derived from
  // the rows present — NOT the full column options — so a guest who only sees a
  // few assigned rows doesn't get a board-wide year strip that defaults to the
  // current year and hides their rows when they live in another year.
  const sectionCol = effectiveGroupBy
    ? board.columns.find((c) => c.id === effectiveGroupBy)
    : undefined;
  const availableYears = useMemo(() => {
    if (sectionCol?.type !== "status" || !sectionCol.options) return [];
    const optById = new Map(sectionCol.options.map((o) => [o.id, o]));
    const ys = new Set<number>();
    for (const r of board.rows) {
      if (r.parentRowId) continue; // sections are top-level only
      const v = r.raw?.cells?.find((c) => c.column_def_id === sectionCol.id)?.value;
      const opt = typeof v === "string" ? optById.get(v) : undefined;
      if (!opt) continue;
      const y = deriveSectionYear(opt.label, opt.goal_date);
      if (y != null) ys.add(y);
    }
    return Array.from(ys).sort((a, b) => b - a);
  }, [sectionCol, board.rows]);

  // Year tab is sticky per user, per board (localStorage) — same pattern as group-collapse
  // (`flow.groups.collapsed.<id>`) and sub-item expand (`flow.tree.expanded.<id>`). Without
  // this the current-year default forced anyone whose tables have no year back to "Alle" on
  // every visit.
  const yearKey = `flow.year.tab.${board.id}`;
  const [yearFilter, setYearFilter] = useState<number | null>(null);
  const yearInit = useRef(false);
  useEffect(() => {
    yearInit.current = false;
    setYearFilter(null);
  }, [board.id]);
  useEffect(() => {
    // Resolve only once the tabs actually render (>= 2 years) — the same gate as the tab bar.
    // A board with a single (unswitchable) year therefore stays on Alle instead of silently
    // hiding its year-less sections behind a tab the user can't reach.
    if (yearInit.current || availableYears.length < 2) return;
    yearInit.current = true;
    let saved: string | null = null;
    try {
      saved = window.localStorage.getItem(yearKey);
    } catch {
      /* localStorage may be unavailable */
    }
    setYearFilter(resolveInitialYearTab(saved, availableYears, new Date().getFullYear()));
  }, [availableYears, yearKey]);
  // The selected year's sections can disappear mid-session ("Gruppe löschen", a colleague
  // deleting rows): fall back to Alle instead of a blank board with the tab bar gone.
  useEffect(() => {
    setYearFilter((y) => reconcileYearFilter(y, availableYears));
  }, [availableYears]);

  // Persist the user's tab choice as they switch (Alle → "all", else the year).
  const selectYear = useCallback(
    (y: number | null) => {
      setYearFilter(y);
      try {
        window.localStorage.setItem(yearKey, y == null ? "all" : String(y));
      } catch {
        /* localStorage may be unavailable */
      }
    },
    [yearKey],
  );

  // Keep the open row's data in sync with board updates.
  useEffect(() => {
    if (!detailRow) return;
    const fresh = board.rows.find((r) => r.id === detailRow.id);
    if (fresh && fresh !== detailRow) setDetailRow(fresh);
  }, [board.rows, detailRow]);

  // Deep-link: when the URL carries ?row=ID and that row is in the board's
  // data, auto-open the detail panel — ONCE per openRowId value. We track the
  // last auto-opened id in a ref (reset when ?row= clears) instead of keying off
  // detailRow. Keying off detailRow re-opened the panel the instant the user
  // closed it (detailRow→null re-ran the effect while ?row= hadn't cleared yet),
  // which is what forced the "needs a second click to close" behavior.
  const lastAutoOpenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openRowId) {
      lastAutoOpenRef.current = null;
      return;
    }
    if (lastAutoOpenRef.current === openRowId) return;
    const match = board.rows.find((r) => r.id === openRowId);
    if (match) {
      lastAutoOpenRef.current = openRowId;
      setDetailRow(match);
    }
  }, [openRowId, board.rows]);

  // Jump-to-comment (①): capture the comment id from the URL ONCE per value into
  // local state, then ask the parent to strip `?comment=` from the URL. Keeping it
  // in state (not reading the URL directly) means clearing the URL doesn't yank the
  // prop out from under RowComments before it flashes — and because the URL is now
  // clean, manually re-opening the same row can't re-flash. Keyed on the comment id
  // (its own ref) so searching a comment on the ALREADY-open row still fires.
  const [focusComment, setFocusComment] = useState<string | null>(null);
  // The search term that led to the focused comment — highlighted inside its body.
  // Held in state (like focusComment) so stripping ?q= from the URL doesn't yank it
  // out before RowComments marks the term.
  const [focusCommentQuery, setFocusCommentQuery] = useState<string | null>(null);
  const lastFocusCommentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openCommentId) {
      lastFocusCommentRef.current = null;
      return;
    }
    if (lastFocusCommentRef.current === openCommentId) return;
    lastFocusCommentRef.current = openCommentId;
    setFocusComment(openCommentId);
    setFocusCommentQuery(openCommentQuery);
    onCommentConsumed?.();
  }, [openCommentId, openCommentQuery, onCommentConsumed]);

  function selectDetail(row: ViewRow | null) {
    setDetailRow(row);
    // A manual row switch/close clears any pending comment focus so a stale flash
    // can't fire when a different row's thread loads.
    setFocusComment(null);
    setFocusCommentQuery(null);
    onDetailChange?.(row?.id ?? null);
  }

  if (!isDesktop) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <MobileBoardView
          board={displayBoard}
          // 🔴 The UNFILTERED board for the detail sheet's Unteraufgaben: displayBoard has had
          // filtered-out rows and hidden columns removed, and a missing Status column would
          // silently cost the sub-tasks their checkbox.
          fullBoard={board}
          onPatchRow={onPatchRow}
          onPatchCell={onPatchCell}
          onCreateSubtask={onCreateSubtask}
          // Gate on real write capability, not just isGuest: mobile delete is a NEW affordance,
          // so a shared-in VIEWER (access "member" but canWrite false) must NOT get a trash that
          // 403s — don't instantiate the desktop "viewer trap" on a surface that never had it.
          onDeleteRow={isGuest || board.canWrite === false ? undefined : onDeleteRow}
          groupByStatus={effectiveGroupBy !== null}
          onAddRow={isGuest || isTickets ? undefined : onAddRow}
          focusCommentId={focusComment ?? undefined}
          focusCommentQuery={focusCommentQuery ?? undefined}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--surface)",
        borderRadius: 10,
        border: "1px solid var(--hairline)",
        boxShadow: "0 1px 0 rgba(13,62,99,0.02)",
        overflow: "hidden",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <BoardSubheader
        board={board}
        boardId={boardId}
        workspaceId={workspaceId}
        onAddRow={isGuest || isTickets ? undefined : onAddRow}
        groupColumnId={effectiveGroupBy}
        onAddColumnOption={!isGuest && canAddColumnOption ? onAddColumnOption : undefined}
      />
      {board.access === "guest" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            fontSize: 12.5,
            color: "var(--ink-2)",
            background: "var(--surface-soft)",
            borderBottom: "1px solid var(--hairline)",
          }}
        >
          <Icon name="eye" size={14} color="var(--ink-3)" />
          {t.guestBoardBanner}
        </div>
      ) : board.access === "shared" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            fontSize: 12.5,
            color: "var(--ink-2)",
            background: "var(--surface-soft)",
            borderBottom: "1px solid var(--hairline)",
          }}
        >
          <Icon name="group" size={14} color="var(--ink-3)" />
          {t.sharedBoardBanner}
        </div>
      ) : null}
      <BoardToolbar
        view={view}
        setView={setView}
        groupByColumnId={effectiveGroupBy}
        groupableColumns={groupableColumns}
        onGroupByChange={changeGroupBy}
        allColumns={allColumns}
        hiddenColumnIds={hiddenColumnIds}
        onToggleColumn={toggleColumn}
        filterColumns={filterColumns}
        filters={filters}
        onFiltersChange={changeFilters}
        sortColumns={sortColumns}
        sort={sort}
        onSortChange={changeSort}
        onUndo={onUndo}
        onRedo={onRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        onNewTicket={isTickets ? onNewTicket : undefined}
      />
      {view === "table" && !isTickets && availableYears.length >= 2 ? (
        <div
          style={{
            display: "flex",
            gap: 4,
            alignItems: "center",
            flexWrap: "wrap",
            padding: "8px 14px",
            borderBottom: "1px solid var(--hairline-soft)",
          }}
        >
          <YearTab label="Alle" active={yearFilter === null} onClick={() => selectYear(null)} />
          {availableYears.map((y) => (
            <YearTab
              key={y}
              label={String(y)}
              active={yearFilter === y}
              onClick={() => selectYear(y)}
            />
          ))}
        </div>
      ) : null}
      {view === "kanban" && statusColumns.length >= 2 ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "8px 14px",
            borderBottom: "1px solid var(--hairline-soft)",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t.kanbanGroupByLabel}</span>
          <select
            value={effectiveKanbanCol ?? ""}
            onChange={(e) => changeKanbanColumn(e.target.value)}
            style={{
              height: 28,
              fontSize: 12.5,
              padding: "0 8px",
              borderRadius: 6,
              border: "1px solid var(--hairline)",
              background: "var(--surface)",
              color: "var(--ink-1)",
            }}
          >
            {statusColumns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div ref={splitRef} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "row" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {view === "table" ? (
            <TableView
              board={tableBoard}
              groupColumn={groupColumn}
              density={density}
              pillStyle={pillStyle}
              groupByColumnId={effectiveGroupBy}
              yearFilter={isTickets ? null : yearFilter}
              onPatchRow={onPatchRow}
              onRowClick={selectDetail}
              onAddRow={isGuest || isTickets ? undefined : onAddRow}
              onPatchCell={onPatchCell}
              onAddColumnOption={onAddColumnOption}
              canAddColumnOption={!isGuest && canAddColumnOption}
              canAddColumn={!isGuest}
              onRowPositionChange={onRowPositionChange}
              onMoveRow={isGuest ? undefined : onMoveRow}
              onColumnUpdate={onColumnUpdate}
              onAddSubitem={isGuest ? undefined : onAddSubitem}
              onDeleteRow={isGuest ? undefined : onDeleteRow}
              onCopyRow={isGuest ? undefined : onCopyRow}
              onDuplicateGroup={isGuest ? undefined : onDuplicateGroup}
              onDeleteGroup={
                // Viewers too (read-only role): every per-row delete would 403.
                isGuest || board.canWrite === false || !onDeleteGroupRows
                  ? undefined
                  : (groupColumnId, option) => setGroupDelete({ groupColumnId, option })
              }
              focusRowId={focusRowId}
              onFocusConsumed={onFocusConsumed}
              openRowId={detailRow?.id ?? null}
            />
          ) : null}
          {view === "kanban" ? (
            <KanbanView
              board={displayBoard}
              statusColumnId={effectiveKanbanCol}
              onPatchCell={onPatchCell}
              onRowClick={selectDetail}
              onPatchRow={onPatchRow}
              openRowId={detailRow?.id ?? null}
            />
          ) : null}
          {view === "calendar" ? (
            <CalendarView
              board={displayBoard}
              onRowClick={selectDetail}
              onReschedule={isGuest ? undefined : (id, day) => onPatchRow(id, { due: day })}
            />
          ) : null}
          {view === "timeline" ? (
            <TimelineView board={displayBoard} onRowClick={selectDetail} />
          ) : null}
        </div>
        {detailRow ? (
          <>
            {/* Large view portals a centered modal and needs no splitter / width column
                (board stays full-width behind the dim); docked keeps the resizable split. */}
            {largeDetail ? null : <DetailSplitter active={resizing} onResizeStart={startResize} />}
            <div
              style={
                largeDetail
                  ? { display: "contents" }
                  : {
                      width: `${detailRatio * 100}%`,
                      minWidth: 360,
                      flexShrink: 0,
                      minHeight: 0,
                      display: "flex",
                    }
              }
            >
              <RowDetailPanel
                variant={largeDetail ? "centered" : "docked"}
                onToggleLarge={() => setDetailView(largeDetail ? "dock" : "large")}
                row={detailRow}
                board={board}
                focusCommentId={focusComment ?? undefined}
                focusCommentQuery={focusCommentQuery ?? undefined}
                onClose={() => selectDetail(null)}
                onPatch={(patch) => onPatchRow(detailRow.id, patch)}
                onPatchCell={onPatchCell}
                onCreateSubtask={onCreateSubtask}
                onOpenRow={(rowId) => {
                  // "öffnen" on a sub-task: swap the panel to that row (unfiltered board, so a
                  // child hidden by an active filter still opens).
                  const next = board.rows.find((r) => r.id === rowId);
                  if (next) selectDetail(next);
                }}
                onDelete={
                  onDeleteRow
                    ? () => {
                        onDeleteRow(detailRow.id as UUID);
                        selectDetail(null);
                        showToast(t.rowDeleted, "success");
                      }
                    : undefined
                }
                onDuplicate={
                  onDuplicateRow
                    ? (withComments: boolean) => {
                        onDuplicateRow(detailRow.id as UUID, withComments);
                        showToast(t.rowDuplicated, "success");
                      }
                    : undefined
                }
              />
            </div>
          </>
        ) : null}
      </div>
      <ConfirmDialog
        open={groupDelete !== null}
        onOpenChange={(open) => {
          if (!open) setGroupDelete(null);
        }}
        title={t.groupDeleteTitle}
        message={
          groupDelete && groupDeletePlan
            ? t.groupDeleteMessage(
                groupDelete.option.label,
                groupDeletePlan.rowIds.length,
                groupDeletePlan.subitemCount,
                // Rows the active filter hides go too — say so, or people delete rows
                // they never saw next to a header that counts only the visible ones.
                groupDeletePlan.rowIds.length -
                  planGroupDelete(filteredRows, groupDelete.groupColumnId, groupDelete.option.id)
                    .rowIds.length,
              )
            : ""
        }
        confirmLabel={t.delete}
        tone="danger"
        onConfirm={() => {
          if (groupDelete && groupDeletePlan && onDeleteGroupRows) {
            onDeleteGroupRows(
              groupDelete.groupColumnId as UUID,
              groupDelete.option.id,
              groupDeletePlan.rowIds as UUID[],
            );
          }
          setGroupDelete(null);
        }}
      />
    </div>
  );
}

function YearTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "4px 12px",
        fontSize: 12.5,
        borderRadius: 7,
        border: "none",
        cursor: "pointer",
        fontWeight: active ? 600 : 500,
        background: active ? "var(--color-brand-blue)" : "var(--surface-soft)",
        color: active ? "#fff" : "var(--ink-2)",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      {label}
    </button>
  );
}
